import { ArrowUpRight, GitBranch, Radar, ShieldCheck, Sparkles } from "lucide-react";
import type { ChainId, TokenCandidate } from "../../server/types";
import { CHAIN_LABELS, compactNumber, currency, shortAddress } from "../lib/format";
import { Logo } from "./Logo";
import { SearchBox, type ChainChoice } from "./SearchBox";

interface LandingProps {
  query: string;
  chain: ChainChoice;
  searching: boolean;
  error: string;
  results: TokenCandidate[];
  onQueryChange: (value: string) => void;
  onChainChange: (value: ChainChoice) => void;
  onSearch: () => void;
  onAnalyze: (token: TokenCandidate) => void;
}

const examples: Array<{ chain: ChainId; address: string; label: string }> = [
  { chain: "solana", address: "DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263", label: "BONK · SOL" },
  { chain: "ethereum", address: "0x6982508145454ce325ddbe47a25d4ec3d2311933", label: "PEPE · ETH" },
  { chain: "bsc", address: "0xfb5b838b6cfeedc2873ab27866079ac55363d37e", label: "FLOKI · BSC" },
  { chain: "robinhood", address: "0xd0601ce157db5bdc3162bbac2a2c8af5320d9eec", label: "NVDA · RHC" },
];

export function Landing(props: LandingProps) {
  return (
    <main className="landing">
      <div className="ambient ambient--one" />
      <div className="ambient ambient--two" />
      <div className="bubble-field" aria-hidden="true">
        {Array.from({ length: 16 }, (_, index) => (
          <i key={index} style={{ "--i": index } as React.CSSProperties} />
        ))}
      </div>
      <nav className="landing-nav">
        <Logo />
        <span className="status-pill"><i /> Live on-chain snapshots</span>
      </nav>

      <section className="hero">
        <div className="eyebrow"><Sparkles size={14} /> Wallet relationships, made visible</div>
        <h1>See who <em>really</em><br />holds the token.</h1>
        <p className="hero-copy">
          Map the biggest holders, trace transfers between them, and spot concentrated wallet clusters before they surprise you.
        </p>
        <SearchBox
          query={props.query}
          chain={props.chain}
          busy={props.searching}
          onQueryChange={props.onQueryChange}
          onChainChange={props.onChainChange}
          onSubmit={props.onSearch}
        />
        <div className="example-row">
          <span>Try a live map</span>
          {examples.map((example) => (
            <button
              key={example.address}
              onClick={() =>
                props.onAnalyze({
                  chain: example.chain,
                  address: example.address,
                  name: example.label.split(" · ")[0],
                  symbol: example.label.split(" · ")[0],
                })
              }
            >
              {example.label}<ArrowUpRight size={13} />
            </button>
          ))}
        </div>

        {props.error && <div className="search-error" role="alert">{props.error}</div>}

        {props.results.length > 0 && (
          <div className="result-picker">
            <div className="result-picker-head">
              <span>Choose the exact token</span>
              <small>{props.results.length} matches on supported chains</small>
            </div>
            <div className="result-list">
              {props.results.map((token) => (
                <button key={`${token.chain}:${token.address}`} onClick={() => props.onAnalyze(token)}>
                  <span className={`token-avatar token-avatar--${token.chain}`}>
                    {token.imageUrl ? <img src={token.imageUrl} alt="" /> : token.symbol.slice(0, 2)}
                  </span>
                  <span className="result-identity">
                    <strong>{token.name} <b>${token.symbol}</b></strong>
                    <small>{CHAIN_LABELS[token.chain]} · {shortAddress(token.address, 6)}</small>
                  </span>
                  <span className="result-market">
                    <strong>{currency(token.priceUsd)}</strong>
                    <small>{compactNumber(token.liquidityUsd)} liquidity</small>
                  </span>
                  <ArrowUpRight size={17} />
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="trust-strip" aria-label="How BubbleTrace works">
        <div><Radar /><span><strong>Live distribution</strong><small>Current balances, never a demo dataset</small></span></div>
        <div><GitBranch /><span><strong>Proven connections</strong><small>Links require on-chain transfers</small></span></div>
        <div><ShieldCheck /><span><strong>Evidence, not verdicts</strong><small>Every limitation stays visible</small></span></div>
      </section>
    </main>
  );
}
